/** Which end of the event's window an edit applies to. */
export type EventWindowBoundary = 'start' | 'end'

/** The event's start/end as stored on the form — UTC ISO strings, '' when unset. */
export interface EventWindow {
  startsAt: string
  endsAt: string
}

/**
 * Keeps an event's start and end in order after one of them is edited.
 *
 * The edited end is always honoured — the user just chose it — so when the two
 * invert it is the *other* one that gets cleared, leaving the form incomplete
 * rather than silently holding an impossible window. Mirrors
 * `withOrderedWindow` in RegistrationSetup's General tab.
 *
 * Compared as full INSTANTS, not calendar days, so a same-day end at an
 * earlier clock time is caught too — something a day-granularity
 * `disabledDate` cannot see.
 *
 * An end exactly equal to the start is left alone: a zero-length event is
 * degenerate but not inverted, and clearing the user's input there would be
 * surprising. Only a strictly earlier end triggers the reset.
 *
 * Both values are UTC ISO strings so this stays framework-agnostic and serves
 * web and mobile alike; unparseable input is treated as "no constraint"
 * rather than throwing.
 */
export const orderEventWindow = (
  next: EventWindow,
  edited: EventWindowBoundary
): EventWindow => {
  const { startsAt, endsAt } = next

  if (!startsAt || !endsAt) {
    return next
  }

  const startMs = Date.parse(startsAt)
  const endMs = Date.parse(endsAt)

  if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs >= startMs) {
    return next
  }

  return edited === 'start'
    ? { startsAt, endsAt: '' }
    : { startsAt: '', endsAt }
}
